import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { BasicExampleComponent } from './examples/basic-example.component';
import { AdvancedExampleComponent } from './examples/advanced-example.component';
import { FiltersExampleComponent } from './examples/filters-example.component';
import { SearchExampleComponent } from './examples/search-example.component';
import { SelectionExampleComponent } from './examples/selection-example.component';
import { StickyColumnsExampleComponent } from './examples/sticky-columns-example.component';
import { VirtualScrollingExampleComponent } from './examples/virtual-scrolling-example.component';
import { ExportExampleComponent } from './examples/export-example.component';
import { ExpandableRowsExampleComponent } from './examples/expandable-rows-example.component';
import { ApiModeExampleComponent } from './examples/api-mode-example.component';
import { TreeGridExampleComponent } from './examples/tree-grid-example.component';
import { ResponsiveExampleComponent } from './examples/responsive-example.component';
import { CellRenderersExampleComponent } from './examples/cell-renderers-example.component';
import { RowGroupingExampleComponent } from './examples/row-grouping-example.component';
import { ContextMenuExampleComponent } from './examples/context-menu-example.component';

/**
 * Smart Grid Demo Component
 * Main demo page showcasing all Smart Grid features
 */
@Component({
  selector: 'app-smart-grid-demo',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    BasicExampleComponent,
    AdvancedExampleComponent,
    FiltersExampleComponent,
    SearchExampleComponent,
    SelectionExampleComponent,
    StickyColumnsExampleComponent,
    VirtualScrollingExampleComponent,
    ExportExampleComponent,
    ExpandableRowsExampleComponent,
    ApiModeExampleComponent,
    TreeGridExampleComponent,
    ResponsiveExampleComponent,
    CellRenderersExampleComponent,
    RowGroupingExampleComponent,
    ContextMenuExampleComponent,
  ],
  template: `
    <div class="p-6 max-w-full mx-auto">
      <div class="mb-8">
        <h1 class="text-4xl font-bold mb-2">Smart Grid Component</h1>
        <p class="text-lg text-gray-600 dark:text-gray-400">
          Componente de tabla altamente configurable, type-safe, inspirado en ag-grid.
        </p>
        <div class="mt-4 flex gap-2 flex-wrap">
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Fase 1: Foundation ✓
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Fase 2: Basic Rendering ✓
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Fase 3: Completa ✓
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            Multi-Column Sort ✓
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            Column Filters ✓
          </span>
          <span class="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
            Global Search ✓
          </span>
          <span class="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold">
            Row Selection ✓
          </span>
          <span class="px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm font-semibold">
            Sticky Columns ✓
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            ⚡ Virtual Scrolling ✓
          </span>
          <span class="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-sm font-semibold">
            📊 Excel/CSV Export ✓
          </span>
          <span class="px-3 py-1 bg-lime-100 text-lime-800 rounded-full text-sm font-semibold">
            🔽 Expandable Rows ✓
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            🌐 API Mode ✓
          </span>
          <span class="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
            🌳 Tree Grid ✓
          </span>
          <span class="px-3 py-1 bg-rose-100 text-rose-800 rounded-full text-sm font-semibold">
            📱 Responsive ✓
          </span>
          <span class="px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-sm font-semibold">
            🎨 Custom Cell Renderers ✓
          </span>
          <span class="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold">
            📊 Row Grouping ✓
          </span>
          <span class="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
            🖱️ Context Menu ✓
          </span>
          <span class="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">
            120+ Mock Orders
          </span>
        </div>
      </div>

      <mat-tab-group animationDuration="0ms" class="demo-tabs">
        <mat-tab label="📋 Ejemplo Básico">
          <app-basic-example></app-basic-example>
        </mat-tab>

        <mat-tab label="🚀 Avanzado">
          <app-advanced-example></app-advanced-example>
        </mat-tab>

        <mat-tab label="🔍 Filtros">
          <app-filters-example></app-filters-example>
        </mat-tab>

        <mat-tab label="🔎 Búsqueda">
          <app-search-example></app-search-example>
        </mat-tab>

        <mat-tab label="✅ Selección">
          <app-selection-example></app-selection-example>
        </mat-tab>

        <mat-tab label="📌 Columnas Fijas">
          <app-sticky-columns-example></app-sticky-columns-example>
        </mat-tab>

        <mat-tab label="⚡ Virtual Scrolling">
          <app-virtual-scrolling-example></app-virtual-scrolling-example>
        </mat-tab>

        <mat-tab label="📊 Exportación">
          <app-export-example></app-export-example>
        </mat-tab>

        <mat-tab label="🔽 Filas Expandibles">
          <app-expandable-rows-example></app-expandable-rows-example>
        </mat-tab>

        <mat-tab label="🌐 Modo API">
          <app-api-mode-example></app-api-mode-example>
        </mat-tab>

        <mat-tab label="🌳 Tree Grid">
          <app-tree-grid-example></app-tree-grid-example>
        </mat-tab>

        <mat-tab label="📱 Responsive">
          <app-responsive-example></app-responsive-example>
        </mat-tab>

        <mat-tab label="🎨 Cell Renderers">
          <app-cell-renderers-example></app-cell-renderers-example>
        </mat-tab>

        <mat-tab label="📊 Row Grouping">
          <app-row-grouping-example></app-row-grouping-example>
        </mat-tab>

        <mat-tab label="🖱️ Context Menu">
          <app-context-menu-example></app-context-menu-example>
        </mat-tab>
      </mat-tab-group>
    </div>
  `,
  styles: [`
    ::ng-deep .demo-tabs .mat-mdc-tab-body-wrapper {
      @apply mt-6;
    }
  `],
})
export class SmartGridDemoComponent {}
